import React, { useState, useEffect } from "react";
import { CustomDatePicker, MuiBox } from "components/atoms";
import { Button, Menu } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import dayjs from "dayjs";

import DownArrow from "assets/images/svg-icons/chevron-down.svg";

const ModifiedMenu = ({ sortModal, setSortModal }) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRange, setSelectedRange] = useState(null);

  const open = Boolean(anchorEl);
  const currentYear = dayjs().year();

  const RANGE_CONFIG = {
    today: {
      getDates: () => ({
        startDate: dayjs().startOf("day"),
        endDate: dayjs().endOf("day"),
      }),
    },
    last7Days: {
      getDates: () => ({
        startDate: dayjs().subtract(7, "days").startOf("day"),
        endDate: dayjs().endOf("day"),
      }),
    },
    last30Days: {
      getDates: () => ({
        startDate: dayjs().subtract(30, "days").startOf("day"),
        endDate: dayjs().endOf("day"),
      }),
    },
    thisYear: {
      getDates: () => ({
        startDate: dayjs(`${currentYear}-01-01`).startOf("day"),
        endDate: dayjs().endOf("day"),
      }),
    },
    lastYear: {
      getDates: () => ({
        startDate: dayjs(`${currentYear - 1}-01-01`).startOf("day"),
        endDate: dayjs(`${currentYear - 1}-12-31`).endOf("day"),
      }),
    },
  };

  const ranges = [
    { key: "today", label: t("common.today") },
    { key: "last7Days", label: t("modal.filter.last7Days") },
    { key: "last30Days", label: t("modal.filter.last30Days") },
    {
      key: "thisYear",
      label: t("filter.thisYear", { year: currentYear }),
    },
    {
      key: "lastYear",
      label: t("filter.lastYear", { year: currentYear - 1 }),
    },
    {
      key: "customRange",
      label: t("filter.customRange"),
    },
  ];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getSelectedRange = (after, before, date) => {
    if (date === "today") return "today";
    if (!after && !before) return null;

    const start = dayjs(after);
    const end = dayjs(before);

    const matchedRange = Object.entries(RANGE_CONFIG).find(([, config]) => {
      const { startDate, endDate } = config.getDates();

      return start.isSame(startDate) && end.isSame(endDate);
    });

    return matchedRange?.[0] || "customRange";
  };

  const formik = useFormik({
    initialValues: {
      startDate: sortModal?.after ? dayjs(sortModal.after) : null,
      endDate: sortModal?.before ? dayjs(sortModal.before) : null,
    },
    onSubmit: ({ startDate, endDate }) => {
      // Explicit "Today" preset only → send date: "today"
      if (selectedRange === "today") {
        setSortModal({
          ...sortModal,
          date: "today",
          after: null,
          before: null,
        });
        handleClose();
        return;
      }

      // Custom range (including both dates = today) and other presets
      // → always send after / before as day boundaries
      const start = startDate ? dayjs(startDate).startOf("day") : null;
      const end = endDate ? dayjs(endDate).endOf("day") : null;

      setSortModal({
        ...sortModal,
        date: null,
        after: start?.valueOf() || null,
        before: end?.valueOf() || null,
      });

      handleClose();
    },
  });

  useEffect(() => {
    setSelectedRange(
      getSelectedRange(sortModal?.after, sortModal?.before, sortModal?.date),
    );

    if (sortModal?.date === "today") {
      const { startDate, endDate } = RANGE_CONFIG.today.getDates();
      formik.setValues({ startDate, endDate });
      return;
    }

    formik.setValues({
      startDate: sortModal?.after
        ? dayjs(sortModal.after).startOf("day")
        : null,
      endDate: sortModal?.before
        ? dayjs(sortModal.before).endOf("day")
        : null,
    });
  }, [sortModal]);

  useEffect(() => {
    if (!selectedRange || selectedRange === "customRange") {
      return;
    }

    const { startDate, endDate } = RANGE_CONFIG[selectedRange].getDates();

    formik.setValues({
      startDate,
      endDate,
    });
  }, [selectedRange]);

  return (
    <>
      <Button
        onClick={handleClick}
        endIcon={
          <MuiBox
            sx={{
              height: "20px",
              width: "20px",
              display: "flex",
            }}
          >
            <img src={DownArrow} alt="" className="img-full" />
          </MuiBox>
        }
        sx={{
          p: "8px 16px",
          color: "text.darkBlack",
          backgroundColor: "background.blue200",
          borderRadius: "8px",
          width: "100%",
          maxWidth: "110px",
          flexShrink: 0,
          justifyContent: "space-between",
        }}
      >
        {t("filter.modified")}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          disablePadding: true,
        }}
        slotProps={{
          paper: {
            sx: {
              mt: "8px",
              boxShadow: "0px 6px 24px 0px #0000000D",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "background.gray400",
              backgroundColor: "background.gray400",
            },
          },
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <MuiBox
            sx={{
              display: "flex",
              gap: "1px",
            }}
          >
            <MuiBox
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "background.white",
                p: "8px",
              }}
            >
              {ranges.map(({ key, label }) => (
                <Button
                  key={key}
                  variant="text"
                  onClick={() => setSelectedRange(key)}
                  sx={{
                    justifyContent: "flex-start",
                    fontWeight: 400,
                    fontSize: "14px",
                    color: "text.darkBlack",
                    borderRadius: "8px",
                    backgroundColor:
                      selectedRange === key
                        ? "background.blue200"
                        : "transparent",
                  }}
                >
                  {label}
                </Button>
              ))}
            </MuiBox>

            {selectedRange === "customRange" && (
              <MuiBox
                sx={{
                  backgroundColor: "background.white",
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                  p: "8px",
                }}
              >
                <CustomDatePicker
                  label={t("common.after")}
                  isBorderColor
                  value={formik.values.startDate}
                  disableFuture={true}
                  maxDate={formik.values.endDate || undefined}
                  onChange={(value) =>
                    formik.setFieldValue(
                      "startDate",
                      value ? dayjs(value).startOf("day") : null,
                    )
                  }
                />

                <CustomDatePicker
                  label={t("common.before")}
                  isBorderColor
                  disableFuture={true}
                  minDate={formik.values.startDate || undefined}
                  value={formik.values.endDate}
                  onChange={(value) =>
                    formik.setFieldValue(
                      "endDate",
                      value ? dayjs(value).endOf("day") : null,
                    )
                  }
                />
              </MuiBox>
            )}
          </MuiBox>

          <MuiBox
            sx={{
              display: "flex",
              justifyContent: "end",
              gap: "12px",
              backgroundColor: "background.gray200",
              p: "16px 24px",
            }}
          >
            <Button variant="whiteContained" onClick={handleClose}>
              {t("commonButton.cancel")}
            </Button>

            <Button
              variant="blueContained"
              type="submit"
              disabled={
                !selectedRange ||
                (selectedRange === "customRange" &&
                  (!formik.values.startDate || !formik.values.endDate))
              }
            >
              {t("filter.apply")}
            </Button>
          </MuiBox>
        </form>
      </Menu>
    </>
  );
};

export default ModifiedMenu;